import {type FirebaseApp, initializeApp} from "@firebase/app"
import {
    FacebookAuthProvider,
    GoogleAuthProvider,
    TwitterAuthProvider,
    signInWithRedirect,
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    onAuthStateChanged,
    getRedirectResult,
    getAdditionalUserInfo,
    sendEmailVerification,
    sendPasswordResetEmail,
    fetchSignInMethodsForEmail,
    signOut,
    updateProfile,
    Auth as FirebaseAuth,
    User as FirebaseUser,
    UserCredential,
    AuthProvider,
    getAuth,
    updatePassword,
    browserPopupRedirectResolver
} from "@firebase/auth";

import {IUsuario} from "services-comun-ed/modules/services/usuarios/interface";
import {IComponentWindow} from "../login/interface";
import {UserHeaderController} from "../login/inicio";
import {AutenticacionPopupController} from "../login/autenticacion";
import {SubmenuController} from "../login/submenu";
import {ValidacionPopupController} from "../login/validacion";
import {SugerenciaPopupController} from "../login/sugerencia";
import {RestablecerPassPopupController} from "../login/restablecer-pass";
import {RestablecerPassInfoPopupController} from "../login/restablecer-pass-info";

import {WebAuthWebFrontendRequest} from "services-comun-ed/modules/services/usuarios/web-auth/frontend-web";
import {NotificationsController} from "../../utils/notifications";

export class Auth {
    /* STATIC */
    private static AUTH?: Auth;

    /* INSTANCE */
    public fbID: string;
    public fbMail: string;
    public userEd?: IUsuario;
    public userFb?: FirebaseUser;

    private readonly firebaseApp: FirebaseApp;
    private readonly firebaseAuth: FirebaseAuth;
    private loginPopup?: IComponentWindow;
    private validacionPopup?: IComponentWindow;
    private sugerenciaPopup?: IComponentWindow;
    private submenu?: IComponentWindow;
    private readonly currentPage: string = "";

    public static async build(): Promise<void> {
        this.AUTH ??= new this();
        await UserHeaderController.build();
        await AutenticacionPopupController.build(this.AUTH);
        await RestablecerPassPopupController.build(this.AUTH);
        await RestablecerPassInfoPopupController.build(this.AUTH);
    }

    public static get(): Auth|undefined {
        return this.AUTH;
    }

    public constructor() {
        this.fbID = "";
        this.fbMail = "";
        this.currentPage = window.location.pathname;

        Object.defineProperty((browserPopupRedirectResolver as any).prototype, '_shouldInitProactively', {
            get: function () {
                return false;
            },
        });

        this.firebaseApp = initializeApp(require("./firebase.json"));
        this.firebaseAuth = getAuth(this.firebaseApp);

        /*
        * getRedirectResult es utilizado por terceros (Google, Facebook, X)
        * El único acceso que no utiliza esta función es signInWithEmailAndPassword
         */
        getRedirectResult(this.firebaseAuth).then(async (result) => {
            if (result) {
                await this.getRedirectResultFlow(result);
            } else {
                onAuthStateChanged(this.firebaseAuth, async (user) => {
                    if (user !== null) {
                        await this.onAuthStateChangedFlow(user);
                    }
                });
            }
        }).catch((error) => {
            //if (error.code === 'auth/account-exists-with-different-credential') this.accountExists(this.firebaseAuth, error);
        });
    }

    private async loadAllComponents(user: IUsuario): Promise<void> {
        this.loginPopup = AutenticacionPopupController.instance.getComponent();
        const submenu = await SubmenuController.build(user.email, Auth.get() as Auth);
        this.submenu = await submenu.getComponent();
        const validacionPopup = await ValidacionPopupController.build();
        this.validacionPopup = await validacionPopup.getComponent();
        const sugerenciaPopup = await SugerenciaPopupController.build();
        this.sugerenciaPopup = await sugerenciaPopup.getComponent();
    }

    private async getRedirectResultFlow(result: UserCredential): Promise<void> {
        this.userFb = result.user;
        const additionalUserInfo = getAdditionalUserInfo(result);
        const autoValidation = this.isAutoValidated(this.userFb?.providerData[0].providerId??"");
        // La primera parte de la condición es utilizada por todos los proveedores excepto Google, para Google es la segunda, ya que la cuenta se encuentra autovalidada desde un principio
        this.userEd = await this.runActions();
        await this.loadAllComponents(this.userEd);
        if (autoValidation && additionalUserInfo && additionalUserInfo.isNewUser) {
            await this.update({nombre: this.userEd.nombre, email: this.userEd.email, enabled: true}, false);
        } else {
            if (!this.userFb?.emailVerified) this.sendEmailValidation(this.userEd, this.userFb);
        }
        this.resetCookieFb();
        this.setCookieFb(await this.userFb!.getIdToken());
        this.resetCookieFav();
        const favoritos = this.userEd.configuracion?.registro_inicial?.equipos_favoritos?.favoritos?.registro as string;
        if (favoritos && favoritos.length > 0) this.setCookieFav(favoritos);
        if (this.isUserPage(this.currentPage)) window.location.reload();
        this.initMenu();
        if (this.userFb?.emailVerified) {
            //this.setPremiumUser();
            //this.checkPremiumUser(this.userEd);
            this.initUser(this.userEd);
        }
    }

    private async onAuthStateChangedFlow(userFb: FirebaseUser): Promise<void> {
        this.userFb = userFb;
        this.userEd = await this.runActions();
        await this.loadAllComponents(this.userEd);
        if (this.userFb?.emailVerified) {
            //this.setPremiumUser();
            //this.checkPremiumUser(this.userEd);
            this.initUser(this.userEd);
        } else {
            if (!this.isExcludePage(this.currentPage)) {
                const frecuencia_registro = this.userEd.configuracion?.registro_inicial?.popup_validacion?.frecuencia?.registro as number;
                const frecuencia_value = this.userEd.configuracion?.registro_inicial?.popup_validacion?.frecuencia?.value as number;
                if (frecuencia_registro && frecuencia_registro >= 1 && frecuencia_registro < frecuencia_value) {
                    (<number>this.userEd.configuracion!.registro_inicial!.popup_validacion!.frecuencia!.registro) += 1;
                    await this.update(this.userEd, false);
                    this.validacionPopup?.window.querySelector("#sendManualEmailValidation")?.addEventListener("click", () => this.sendManualEmailValidation(this.userFb!));
                    this.validacionPopup!.window.style.display = "block";
                    UserHeaderController.instance.createOverlay(this.validacionPopup!.window);
                }
                this.sendEmailValidation(this.userEd, this.userFb);
            }
        }
        this.initMenu();
        this.resetCookieFb();
        this.setCookieFb(await this.userFb!.getIdToken());
        this.resetCookieFav();
        const favoritos = this.userEd.configuracion?.registro_inicial?.equipos_favoritos?.favoritos?.registro as string;
        if (favoritos && favoritos.length > 0) this.setCookieFav(favoritos);
    }

    private initMenu(): void {
        this.loginPopup?.window.classList.remove("enabled");
        this.loginPopup!.window.style.display = "";
        this.submenu?.window.classList.add("enabled");
    }

    private initSuggestWindow(userEd: IUsuario): void {
        const favoritos = userEd.configuracion?.registro_inicial?.equipos_favoritos?.favoritos?.registro as string;
        const frecuencia_registro = userEd.configuracion?.registro_inicial?.popup_sugerencia?.frecuencia?.registro as number;
        const frecuencia_value = userEd.configuracion?.registro_inicial?.popup_sugerencia?.frecuencia?.value as number;
        const mostrar_registro = userEd.configuracion?.registro_inicial?.popup_sugerencia?.mostrar?.registro as number;
        const fecha_actual = new Date().getTime();
        const fecha_actual_inc = fecha_actual + (24 * 60 * 60 * 1000);
        if (!mostrar_registro || mostrar_registro < fecha_actual && frecuencia_registro < frecuencia_value && favoritos && favoritos.length === 0) {
            // @ts-ignore
            userEd.configuracion!.registro_inicial!.popup_sugerencia!.frecuencia!.registro! += 1;
            userEd.configuracion!.registro_inicial!.popup_sugerencia!.mostrar!.registro = fecha_actual_inc;
            this.update(userEd, false).then(() => {
                this.sugerenciaPopup!.window.style.display = "block";
                UserHeaderController.instance.createOverlay(this.sugerenciaPopup!.window);
                this.sugerenciaPopup?.buttons?.go_page?.addEventListener("click", () => {
                    window.location.href = "/usuario/?section=mis-equipos";
                });
            });
        }
    }

    private isExcludePage(fromPage: string): boolean {
        const pages = ["/usuario/", "/acciones-usuario/"];
        return !!pages.includes(fromPage);
    }

    private isUserPage(fromPage: string): boolean {
        const pages = ["/usuario/"];
        return !!pages.includes(fromPage);
    }

    private sendEmailValidation(userEd: IUsuario, user: FirebaseUser): void {
        if (!userEd.enabled) {
            const frecuencia_registro = userEd.configuracion?.registro_inicial?.popup_validacion?.frecuencia?.registro as number;
            if (!frecuencia_registro) {
                // @ts-ignore
                userEd.configuracion!.registro_inicial!.popup_validacion!.frecuencia!.registro! = 1;
                sendEmailVerification(user)
                    .then(() => {
                        this.update(userEd, false).then(() => {
                            this.validacionPopup!.window.style.display = "block";
                            UserHeaderController.instance.createOverlay(this.validacionPopup!.window);
                            this.validacionPopup?.window.querySelector("#sendManualEmailValidation")?.addEventListener("click", () => this.sendManualEmailValidation(user));
                        });
                    });
            }
        }
    }

    public sendManualEmailValidation(user: FirebaseUser): void {
        sendEmailVerification(user)
            .then(() => {
                NotificationsController.build("info", "E-mail enviado correctamente.");
            });
    }

    private isAutoValidated(provider: string): boolean {
        switch (provider) {
            case "google.com":
                return true;
            default:
                return false;
        }
    }

    private async runActions(): Promise<IUsuario> {
        return await this.checkAccessWithBackend().then((user) => {
            return user;
        }).catch((error) => {
            return Promise.reject(error);
        });
    }

    public async accessWithGoogle(): Promise<void> {
        const provider = new GoogleAuthProvider();
        provider.setCustomParameters({prompt: 'select_account'});
        await this.signIn(this.firebaseAuth, provider);
    }

    public async accessWithFacebook(): Promise<void> {
        await this.signIn(this.firebaseAuth, new FacebookAuthProvider());
    }

    public async accessWithX(): Promise<void> {
        await this.signIn(this.firebaseAuth, new TwitterAuthProvider());
    }

    public async accessWithCredentials(email: string, password: string, action: string, name?: string): Promise<void> {
        if (action === "accion-login") {
            await signInWithEmailAndPassword(this.firebaseAuth, email, password).then(()=>{
                UserHeaderController.instance.destroyOverlay();
            }).catch((error) => {
                this.showErrors(error);
            });
        } else {
            await createUserWithEmailAndPassword(this.firebaseAuth, email, password).then(()=> {
                UserHeaderController.instance.destroyOverlay();
                updateProfile(this.userFb!, {
                    displayName: name,
                });
            }).catch((error) => {
                this.showErrors(error);
            });
        }
    }

    private showErrors(error: any) : void {
        if (error.code === 'auth/requires-recent-login') {
            NotificationsController.build("error", "Vuelve a iniciar sesión para realizar esta acción.");
        }
        if (error.code === 'auth/invalid-email') {
            NotificationsController.build("error", "La dirección de email proporcionada es inválida.");
        }
        if (error.code === 'auth/missing-password') {
            NotificationsController.build("error", "La contraseña proporcionada es inválida.");
        }
        if (error.code === 'auth/user-disabled') {
            NotificationsController.build("error", "El inicio de sesión ha sido deshabilitado para esta dirección de email.");
        }
        if (error.code === 'auth/user-not-found') {
            NotificationsController.build("error", "No se ha encontrado ninguna cuenta con la dirección de email proporcionada.");
        }
        if (error.code === 'auth/email-already-in-use') {
            NotificationsController.build("error", "Esta dirección de email ya está siendo utilizada.");
        }
        if (error.code === 'auth/wrong-password') {
            NotificationsController.build("error", "La contraseña no es correcta.");
        }
        if (error.code === 'auth/weak-password') {
            NotificationsController.build("error", "La contraseña es débil, intentalo con otra más segura.");
        }
    }

    private resetCookieFb(): void {
        document.cookie = "fbId=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    }

    public resetCookieFav(): void {
        document.cookie = "favUser=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    }

    private setCookieFb(token: string): void {
        document.cookie = "fbId=" + token + "; path=/;";
    }

    public setCookieFav(favorito: string): void {
        document.cookie = "favUser=" + favorito + "; path=/;";
    }

    private async signIn(auth: FirebaseAuth, provider: AuthProvider): Promise<void> {
        await signInWithRedirect(auth, provider);
    }

    private async accountExists(auth: FirebaseAuth, error: any): Promise<void> {
        if (error.code === 'auth/account-exists-with-different-credential') {
            const email = error.customData.email;
            await fetchSignInMethodsForEmail(auth, email).then(async (methods) => {
                await NotificationsController.build("error", "Ya existe una cuenta con el correo electrónico introducido utilizando el proveedor " + methods[0]);
                this.loginPopup!.window.style.display = "block";
            });
        }
    }

    public async resetPassword(email: string): Promise<void> {
        if (email.length > 0) {
            await sendPasswordResetEmail(this.firebaseAuth, email).then(() => {
                NotificationsController.build("info", "E-mail enviado correctamente.");
            }).catch((error) => {
                this.showErrors(error);
                return Promise.reject(error);
            });
        } else {
            await NotificationsController.build("error", "Es obligatorio introducir un correo electrónico.");
            return Promise.reject("Es obligatorio introducir un correo electrónico.");
        }
    }

    public async logout() : Promise<void> {
        await signOut(this.firebaseAuth).then(async () => {
            this.resetCookieFb();
            this.resetCookieFav();
            window.location.href = "/";
        }).catch(async (error) => {
            Promise.reject(error);
        });
    }

    public async changePassword(user: FirebaseUser, newPassword: string): Promise<void> {
        if (newPassword.length > 0) {
            await updatePassword(user, newPassword).then(()=>{
                NotificationsController.build("info", "Contraseña cambiada correctamente.");
            }).catch((error) => {
                this.showErrors(error);
            });
        } else {
            NotificationsController.build("error", "Es obligatorio introducir una nueva contraseña.");
        }
    }

    public async update(user: any, notify: boolean = true): Promise<any> {
        const idToken = await this.userFb?.getIdToken(true) as string;
        await WebAuthWebFrontendRequest.editarUsuario(idToken, user).then(() => {
            if (notify) NotificationsController.build("info", "Cambios guardados correctamente.");
        }).catch(() => {
            NotificationsController.build("error", "Error al guardar cambios.");
        });
    }

    public async delete(): Promise<any> {
        const idToken = await this.userFb?.getIdToken(true) as string;
        await WebAuthWebFrontendRequest.bajaUsuario(idToken, this.fbMail);
        await this.userFb?.delete()
        await this.logout();
    }

    private async checkAccessWithBackend(): Promise<IUsuario> {
        const idToken = await this.userFb?.getIdToken(true) as string;
        return await WebAuthWebFrontendRequest.login(idToken);
    }

    /*private setPremiumUser(): void {
        localStorage.setItem("mr-user-premium", "1");
    }*/

    /*private checkPremiumUser(userEd: IUsuario): void {
        window.__tcfapiprivate('rejectAndSaveConsents', null, async (event: any) => {
            if (event) {
                this.fbMail = userEd.email as string;
                if (!this.isExcludePage(this.currentPage)) this.initSuggestWindow(userEd);
            }
        });
    }*/

    private initUser(userEd: IUsuario): void {
        if (localStorage.getItem("mr-user-premium")) localStorage.removeItem("mr-user-premium");
        this.fbMail = userEd.email as string;
        if (!this.isExcludePage(this.currentPage)) this.initSuggestWindow(userEd);
    }

}
